import clsx from "clsx";
import { useCombobox } from "downshift";
import type { Entity } from "gamedata/GameData";
import { useRef } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { useFetcher, useNavigate } from "react-router";
import type { lstring } from "~/util/Lang";
import { useT, useTurl } from "~/util/Lang";
import { Icon } from "../ui/Icon";

import css from "./SearchBox.module.css";

export function SearchBox() {
  const t = useT();
  const turl = useTurl();
  const navigate = useNavigate();
  const cities = useFetcher<Entity[]>();
  const {
    isOpen,
    getMenuProps,
    getInputProps,
    highlightedIndex,
    getItemProps,
    reset,
  } = useCombobox({
    id: "search-box",
    items: cities.data ?? [],
    itemToString: () => "",
    defaultHighlightedIndex: 0,
    onInputValueChange: (event) => {
      cities.load(turl("/search?q=" + event.inputValue));
    },
    onSelectedItemChange: (changes) => {
      if (changes.selectedItem) {
        const url = turl(getEntityUrl(changes.selectedItem)!);
        if (url) {
          reset();
          navigate(url);
        }
      }
    },
  });

  const inputRef = useRef<HTMLInputElement | null>(null);
  useHotkeys("/", () => {
    setTimeout(() => {
      inputRef.current?.focus();
    }, 0);
  });

  const inputProps = getInputProps({
    ref: (el) => {
      inputRef.current = el;
    },
    className: clsx(css.input, {
      [css.inputOpen]: isOpen && cities.data,
    }),
    placeholder: t({ en: "Search [/]", ru: "Поиск [/]" }),
  });
  const menuProps = getMenuProps({}, { suppressRefError: true });

  return (
    <div className={css.root}>
      <div>
        <input {...inputProps} />
        {cities.state === "submitting"
          ? t({ en: "Searching...", ru: "Идет поиск..." })
          : null}
      </div>

      <div style={{ position: "relative" }}>
        {cities.data && isOpen ? (
          <div
            style={{
              position: "absolute",
              top: 4,
              right: -2,
              minWidth: "calc(100% + 4px)",
              zIndex: 1000,
            }}
          >
            <div className={css.menu} {...menuProps}>
              {cities.data.length ? (
                cities.data.map((item, index) => {
                  const url = turl(getEntityUrl(item)!);
                  let type = getEntityType(item);
                  return (
                    <a
                      key={index}
                      href={url}
                      className={clsx([css.item], {
                        [css.itemActive]: highlightedIndex === index,
                      })}
                      {...getItemProps({ item, index })}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                        }}
                      >
                        <div
                          style={{
                            alignItems: "center",
                            display: "flex",
                            gap: 4,
                            overflow: "hidden",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {item.icon ? (
                            <Icon icon={item.icon} size={16} />
                          ) : (
                            <div style={{ width: 16, height: 16 }} />
                          )}
                          {item.name}
                          {item.level && ` [${item.level}]`}
                        </div>
                        <div
                          style={{
                            alignItems: "center",
                            display: "flex",
                            color: "#999",
                            paddingLeft: "10px",
                            textTransform: "uppercase",
                            fontSize: "11px",
                          }}
                        >
                          {type && t(type)}
                        </div>
                      </div>
                    </a>
                  );
                })
              ) : (
                <div className={css.item}>
                  {t({ en: "No results found", ru: "Ничего не найдено" })}
                </div>
              )}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function getEntityUrl(entity: Entity) {
  switch (entity.type) {
    case "item":
      return "/items/" + entity.id;
    case "quest":
      return "/quests/" + entity.id;
    case "npc":
      return "/npcs/" + entity.id;
    case "skill":
      return "/skills/" + entity.id;
  }
  return null;
}

function getEntityType(entity: Entity): lstring | null {
  switch (entity.type) {
    case "item":
      return { en: "Item", ru: "Предмет" };
    case "quest":
      return { en: "Quest", ru: "Квест" };
    case "npc":
      return { en: "NPC", ru: "NPC" };
    case "skill":
      return { en: "Skill", ru: "Умение" };
  }
  return null;
}
